import Highcharts, {
  Series,
  SeriesAreaOptions,
  SeriesArearangeOptions,
  SeriesLineOptions,
  YAxisOptions,
} from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import debounce from 'lodash/debounce';
import sortBy from 'lodash/sortBy';
import {AttrsSchema, Component} from 'panel';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import ResizeObserver from 'resize-observer-polyfill';

import {HoverCallbackArgs, SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {
  getTooltipPosition,
  hoverSeries,
  patchHighchartsForNegativeLog,
  unHoverChart,
} from 'common/report/util/highcharts';
import {formatMetricNumber, htmlEncodeString} from 'common/util';
import {isIncompleteInterval} from 'common/util/chart';
import * as colors from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DEFAULT_CHART_HEIGHT, DEFAULT_CHART_WIDTH} from 'common/report/constants';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {listenForPropUpdate} from 'common/util/component-helpers';
import {formatDate, MS_BY_UNIT, UNITS} from 'common/util/date';
import {layoutManager} from 'common/report/util/layout-manager';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';

import {DisplayAnalysisType, DisplayPlotStyle, DisplayStackingType, LineStyle, XAxisUnit} from './enums';
import {IChartDataItem, LineChartState, XAxisOptions} from './types';
import {template} from './template';
import css from './index.inline.styl';
import {Annotations} from './annotations';

highchartsMore(Highcharts);
patchHighchartsForNegativeLog(Highcharts);
HighchartsAnnotations(Highcharts);
// XSS risk if we allow untrusted highcharts configuration
Highcharts.AST.bypassHTMLFiltering = true;

function getColorWithAlpha(color: string, alpha: number /* float in range [0, 1] */) {
  return new Highcharts.Color(color).setOpacity(alpha).get(`rgba`) as string;
}

export const LINE_CHART_PROP_KEYS = [
  `chartData`,
  `formatPointX`,
  `formatPointY`,
  `formatTicksX`,
  `formatTooltip`,
  `formatXAxisLabel`,
  `formatYAxisLabel`,
  `onHighChartClick`,
  `onHighchartMouseOut`,
  `onHighchartMouseOver`,
] as const;

type ArrayType<T> = T extends ReadonlyArray<infer U> ? U : never;

export type LineChartProps = Pick<LineChart, ArrayType<typeof LINE_CHART_PROP_KEYS>>;

export interface LineChartAttrs {
  'chart-height': number;
  'chart-width': number;
  'chart-margin-left': number;
  'chart-margin-right': number;
  'color-theme': string;
  'display-analysis-type': DisplayAnalysisType;
  'display-plot-style': DisplayPlotStyle;
  'display-stacking-type': DisplayStackingType;
  'download-mode': boolean;
  'incomplete-to-date': boolean;
  'incomplete-from-date': boolean;
  'hide-y-axis-ticks': boolean;
  'show-annotations': boolean;
  'time-unit': TimeUnit;
  timezone: string;
  'utc-offset': number;
  'x-axis-options': XAxisOptions;
  'y-axis-label-alignment': Highcharts.AlignValue;
  'y-axis-label-horizontal-offset': number;
  'y-axis-label-vertical-offset': number;
  'y-axis-options': Pick<YAxisOptions, 'softMin' | 'softMax' | 'maxPadding'>;
  'zoom-type'?: 'x';
}

export class LineChart extends Component<LineChartState, LineChartAttrs, any, any, ChartsContextRegistry> {
  public chartData: IChartDataItem[];
  public formatPointX?: (x: number) => string;
  public formatPointY?: (y: number) => string;
  public formatTicksX?: (min: number, max: number) => number[];
  public formatYAxisLabel?: (value: number) => string;
  public formatXAxisLabel?: (value: number) => string;
  public onHighchartMouseOut?: (highchartChart: any) => void;
  public onHighchartMouseOver?: (
    highchartChart: any,
    seriesIdx: number,
    dataIdx: number,
    color: string,
    plotX: number,
    plotY: number,
  ) => void;
  public onHighChartClick?: (point: Highcharts.Point) => void;
  // formatTooltip's @tooltipContext argument type is documented here: https://api.highcharts.com/class-reference/Highcharts.TooltipFormatterContextObject
  // formatTooltip's @series argument is the same as tooltipContext.series
  public formatTooltip?: (x: number, y: number, series: any, idx: number, tooltipContext: any) => string;
  public annotations: Annotations;

  public static get attrsSchema(): AttrsSchema<LineChartAttrs> {
    return {
      ...super.attrsSchema,
      'chart-height': {
        type: `number`,
        default: DEFAULT_CHART_HEIGHT,
        description: `height of the chart in px (set to 0 to fill container)`,
      },
      'chart-width': {
        type: `number`,
        default: DEFAULT_CHART_WIDTH,
        description: `width of the chart in px (set to 0 to fill container)`,
      },
      'chart-margin-left': {
        type: `number`,
        default: 50,
        description: `left margin of the chart in px`,
      },
      'chart-margin-right': {
        type: `number`,
        default: 10,
        description: `right margin of the chart in px`,
      },
      'color-theme': {
        type: `string`,
        default: `default`,
        description: `color theme of the lines plotted on the chart`,
      },
      'display-analysis-type': {
        type: `string`,
        default: DisplayAnalysisType.Linear,
        enum: [DisplayAnalysisType.Linear, DisplayAnalysisType.Logarithmic],
      },
      'display-plot-style': {
        type: `string`,
        default: DisplayPlotStyle.Standard,
        enum: [DisplayPlotStyle.Stacked, DisplayPlotStyle.Standard],
        description: `plot style, standard vs stacked`,
      },
      'display-stacking-type': {
        type: `string`,
        default: DisplayStackingType.Normal,
        enum: [DisplayStackingType.Normal, DisplayStackingType.Percent],
        description: `stacking type, only applicable for "stacked" plot style`,
      },
      'download-mode': `boolean`,
      'incomplete-to-date': {
        type: `boolean`,
        description: `zone with to date, overrides incomplete interval check, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'incomplete-from-date': {
        type: `boolean`,
        description: `zone with from date as well, utc offset from project, which is only necessary if you want incomplete interval zoning`,
      },
      'hide-y-axis-ticks': {
        type: `boolean`,
        description: `hide y axis values to the right of the chart`,
      },
      'show-annotations': {
        type: `boolean`,
        description: `shows global annotations on mp-line-chart`,
      },
      'time-unit': {
        type: `string`,
        default: null,
        enum: [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Quarter],
        description: `time unit to align x axis, which is only necessary if you want incomplete interval zoning`,
      },
      timezone: `string`,
      'utc-offset': `number`,
      'x-axis-options': {
        type: `json`,
        description: `x-axis overrides for highcharts of type XAxisOptions`,
      },
      'y-axis-label-alignment': {
        type: `string`,
        description: `adds an alignment for the y axis labels, such as left or right aligned`,
        tsType: `Highcharts.AlignValue`,
      },
      'y-axis-label-horizontal-offset': {
        type: `number`,
        description: `adds a horizontal position offset to the y axis labels`,
      },
      'y-axis-label-vertical-offset': {
        type: `number`,
        description: `adds a vertical position offset to the y axis labels`,
      },
      'y-axis-options': {
        type: `json`,
        default: null,
        description: `y-axis overrides`,
        tsType: `YAxisOption`,
      },
      'zoom-type': {
        type: `string`,
        default: ``,
        enum: [``, `x`],
      },
    };
  }

  constructor() {
    super();
    // filter `formatTooltip` because it is passed to this component as a new anonymous function in the `insights-line-chart` template,
    // which is considered changed on every render cycle.
    for (const prop of LINE_CHART_PROP_KEYS.filter((prop) => prop !== `formatTooltip`)) {
      listenForPropUpdate(this, prop, this.updateChartDebounced);
    }
  }

  private highcharts: Highcharts.Chart;
  private hoveredTimestampMs: any;
  private selfResizeObserver: ResizeObserver;
  private chartHeight: number; // px

  public get config() {
    return {
      css,
      useShadowDom: true,
      template,
      helpers: {
        getChartData: () => this.chartData,
        getChartElement: () => this.el,
        getHighCharts: () => this.highcharts,
        annotations: Annotations.getHelpers(this),
      },
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    if (!this.chartData) {
      throw new Error(`"chartData" is a required component prop`);
    }

    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {height} = entries[0].contentRect;
      this.chartHeight = height;
      // update the entire chart instead of only reflow(), because some chart options may be based on chartHeight
      this.updateChart();
    });
    this.selfResizeObserver.observe(this);

    const chartEl = document.createElement(`div`);
    chartEl.classList.add(`mp-highcharts-root`);
    chartEl.style.width = `100%`;
    chartEl.style.height = `100%`;
    this.el.appendChild(chartEl);

    this.highcharts = new Highcharts.Chart(chartEl, this.getChartOptions());
    // note: this.highcharts.container may not exist in isotest environment
    this.highcharts.container?.addEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
    this.highcharts.container?.addEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
    layoutManager.getConfig().reportScrollContainer?.addEventListener(`scroll`, this.handleReportScrollContainerScroll);
    this.updateChart();

    if (this.attr(`show-annotations`)) {
      this.annotations = new Annotations(this);
      this.update({annotations: this.annotations.getDefaultState()});
    }

    const boundLegendHovered = this.legendHovered.bind(this);
    this.getContext(`segmentFilterController`).subscribeLegendHover(boundLegendHovered);
    this.onDisconnected(() => this.getContext(`segmentFilterController`).unsubscribeLegendHover(boundLegendHovered));
  }

  public disconnectedCallback() {
    this.selfResizeObserver.disconnect();

    if (this.annotations) {
      this.annotations.removeElements();
    }

    layoutManager
      .getConfig()
      .reportScrollContainer?.removeEventListener(`scroll`, this.handleReportScrollContainerScroll);
    if (this.highcharts) {
      this.highcharts.container?.removeEventListener(`mousemove`, this.handleHighchartsContainerMouseMove);
      this.highcharts.container?.removeEventListener(`mouseleave`, this.handleHighchartsContainerMouseLeave);
      this.highcharts.destroy();
      delete this.highcharts;
    }

    super.disconnectedCallback();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    this.updateChartDebounced();
  }

  legendHovered({hovered}: HoverCallbackArgs) {
    if (this.highcharts.container.style.pointerEvents === `none`) {
      // the chart is disabled, like when a popover is open
      return;
    }

    const hoveredSeries = hovered && (this.highcharts.get(hovered.seriesId) as Series);
    if (hoveredSeries) {
      hoverSeries(hoveredSeries);
    } else {
      unHoverChart(this.highcharts);
    }
  }

  getChartData() {
    return this.chartData;
  }

  getHighcharts() {
    return this.highcharts;
  }

  getHoveredTimestampMs() {
    return this.hoveredTimestampMs;
  }

  defaultXAxisLabelFormatter(value: number, unit: TimeUnit) {
    return formatDate(value, {
      unit,
      displayRangeIfWeek: false,
      customFormatting: {
        [UNITS.minute]: `h:mma`,
        [UNITS.hour]: `MMM D, ha`,
        [UNITS.day]: `MMM D`,
        [UNITS.week]: `MMM D`,
        [UNITS.month]: `MMMM`,
        [UNITS.quarter]: `[Q]Q YYYY`,
        [UNITS.year]: `YYYY`,
      },
    });
  }

  private updateChart() {
    if (this.highcharts) {
      this.highcharts.update(
        this.getChartOptions(),
        true, // redraw after update
        true, // oneToOne: add/remove series when they change
      );
      this.highcharts.reflow();

      if (this.chartData.length && !this.state.annotations?.allAnnotations.size && !this.annotations?.areLoaded()) {
        this.annotations?.load(); // note: the annotations load() method implicitly requires highcharts to be initialized
      }
    }
  }

  private updateChartDebounced = debounce(this.updateChart.bind(this), 20, {trailing: true});

  private formatSeries(): Array<
    Omit<SeriesAreaOptions, 'type'> | Omit<SeriesLineOptions, 'type'> | Omit<SeriesArearangeOptions, 'type'>
  > {
    const isStacked = this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked;
    // prettier-ignore
    return this.chartData
      .map((dataSegment, segmentIdx) => {
        const isAreaRange = dataSegment.data?.[0]?.length === 3;
        const colorIdx = segmentIdx % this.chartData.length;
        const seriesColor = dataSegment.color || colors.getChartSegmentColorCombo(colorIdx, this.attr(`color-theme`)).primary;
        const isSinglePoint = dataSegment.data.length === 1;
        return {
          name: dataSegment.name,
          custom: {prop: dataSegment.label},
          ...(dataSegment.id ? {id: dataSegment.id} : {}),
          ...(dataSegment.linkedTo ? {linkedTo: dataSegment.linkedTo} : {}),
          data: dataSegment.data,
          type: isAreaRange ? `arearange` : (isStacked ? `area` : `line`),
          color: seriesColor,
          marker: {
            enabled: isSinglePoint,
          },
          visible: dataSegment.visible,
          dashStyle: dataSegment.dashStyle || LineStyle.Standard,
          incompleteLength: dataSegment.incompleteLength,
          isComparisonItem: dataSegment.isComparisonItem,
          ...(isAreaRange ? {fillOpacity: 0.4} : {}),
        };
      })
      // TODO opslead
      // eslint-disable-next-line sonarjs/cognitive-complexity
      .map((lineSeries) => {
        lineSeries.data = sortBy(lineSeries.data, (d) => d[0]) as Array<[number, number]> | Array<[number, number, number]>;
        const timeUnit = this.attr(`time-unit`);
        let incompletePathsAndZoning = {};
        const dataLength = lineSeries.data.length;
        const showIncompleteToDate = this.attr(`incomplete-to-date`);
        const showIncompleteFromDate = this.attr(`incomplete-from-date`);
        const dashStyle = lineSeries.dashStyle;
        const unspecifiedIncompleteLength = lineSeries.incompleteLength === undefined;
        const incompleteLength =
          unspecifiedIncompleteLength || lineSeries.incompleteLength > dataLength - 1
            ? 1
            : lineSeries.incompleteLength;
        const incompleteZoneOptions = { // for Highcharts configuration
          dashStyle: LineStyle.Incomplete,
          fillColor: getColorWithAlpha(lineSeries.color as string, 0.5),
        };
        if (timeUnit && unspecifiedIncompleteLength) {
          const utcOffset = this.attr(`utc-offset`);
          const isIncompletePath = isIncompleteInterval(lineSeries.data, {
            unit: timeUnit,
            utcOffset,
          });
          const showIncomplete = isIncompletePath && lineSeries.data.length >= 2;
          incompletePathsAndZoning = {
            isIncompletePath,
            ...(showIncomplete && {
              zoneAxis: `x`,
              zones: [
                {value: lineSeries.data[dataLength - 2][0]},
                incompleteZoneOptions,
              ],
            }),
          };
        } else if (dataLength > 1 && incompleteLength > 0 && (showIncompleteFromDate || showIncompleteToDate)) {
          const zones = [];
          if (showIncompleteFromDate && showIncompleteToDate) {
            // Highchart has a bug with [dot, solid, dot]. So when we are changing the line style more then once,
            // we need to make sure we define both the starting and ending position for the solid part of the line.
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength + 1 < dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            } else if (incompleteLength + 1 === dataLength - incompleteLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          } else if (showIncompleteFromDate) {
            zones.push({value: lineSeries.data[incompleteLength][0], ...incompleteZoneOptions});
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[incompleteLength + 1][0], dashStyle});
            }
          } else {
            // Only showIncompleteToDate is true
            if (incompleteLength < dataLength - 1) {
              zones.push({value: lineSeries.data[dataLength - incompleteLength - 1][0], dashStyle});
            }
            zones.push({value: lineSeries.data[dataLength - 1][0], ...incompleteZoneOptions});
          }
          incompletePathsAndZoning = {
            isIncompletePath: true,
            zoneAxis: `x`,
            zones,
          };
        }
        return {
          ...lineSeries,
          ...incompletePathsAndZoning,
          onHighchartMouseOut: this.onHighchartMouseOut,
        };
      });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getChartOptions(): Highcharts.Options {
    const highchartChart = this.highcharts;
    const series = this.formatSeries();
    const xAxisOptions: XAxisOptions = this.attr(`x-axis-options`);
    const yAxisOptions: YAxisOptions = this.attr(`y-axis-options`);
    const timeUnit = this.attr(`time-unit`);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisComponent = this;
    // create a function so returned object can still refer to the most up to date chartData
    const hasChartData = () => Boolean(this.chartData?.length);

    return {
      // TODO consider using the accessibility.js module. This silences warning in the meantime.
      accessibility: {enabled: false},
      chart: {
        animation: false,
        backgroundColor: null,
        // "reflow" is normally true by default, but it only triggers on window resize events,
        // so we disable it and call reflow() manually with a ResizeObserver on this element.
        reflow: false,
        marginLeft: this.attr(`chart-margin-left`),
        marginRight: this.attr(`chart-margin-right`),
        style: {
          fontFamily: mpFonts.fontFamilyPrimary,
          fontSize: `12px,`,
        },
        spacingBottom: (() => {
          if (this.attr(`show-annotations`)) {
            return 32;
          } else if (this.chartHeight > 300) {
            return 15;
          } else {
            return 8;
          }
        })(),
        type: this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked ? `area` : `line`,
        zoomType: this.attr(`zoom-type`),
        events: {
          redraw(this) {
            if (thisComponent.annotations && hasChartData()) {
              thisComponent.annotations.setAnnotations(
                this.xAxis[0].tickPositions[0],
                this.xAxis[0].tickPositions[this.xAxis[0].tickPositions.length - 1],
              );
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      loading: {
        labelStyle: {
          color: `transparent`,
        },
      },
      plotOptions: {
        line: {
          ...(thisComponent.onHighChartClick && {cursor: `pointer`}),
          lineWidth: 2,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          shadow: false,
          states: {
            hover: {
              animation: false,
            },
            normal: {
              // Setting duration to 25ms instead of `animation: false` prevents all lines flashing
              // into the normal state when hovering between densely packed lines (bug in highcharts).
              animation: {duration: 25},
            },
          },
        },
        area: {
          ...(thisComponent.onHighChartClick && {cursor: `pointer`}),
          lineWidth: 0,
          marker: {
            symbol: `circle`,
            lineColor: `#fff`,
            lineWidth: 2,
            radius: 5,
          },
          // default 0.75
          fillOpacity: 0.85,
          shadow: false,
          states: {
            hover: {
              lineWidthPlus: 0,
            },
            normal: {
              animation: {duration: 200},
            },
            inactive: {
              // default 0.2
              opacity: 0.3,
            },
          },
          trackByArea: true,
          stickyTracking: false,
        },
        series: {
          animation: false,
          // specify stacking as undefined, there seems to be a bug when switching from area => line
          // while the chart is still rendered where the stacking from area applies to line.
          stacking: undefined,
          states: {
            inactive: {enabled: true, animation: false},
            select: {
              halo: {
                size: 10,
                opacity: 0.25,
              },
            },
          },
          marker: {
            enabled: false,
            lineWidth: 2,
            symbol: `circle`,
            states: {
              select: {
                lineColor: `#fff`,
                fillColor: null,
                lineWidth: 3,
                radius: 7,
              },
            },
          },
          point: {
            events: {
              mouseOver() {
                thisComponent.updateHoveredTimestampMs(this.x);
                const {color, index: dataIdx, plotX, plotY, series} = this;
                const {index: seriesIdx} = series.options;
                return thisComponent.onHighchartMouseOver?.(
                  highchartChart,
                  seriesIdx,
                  dataIdx,
                  color as string,
                  plotX,
                  plotY,
                );
              },
              click(event) {
                return thisComponent.onHighChartClick?.(event.point);
              },
            },
          },
          shadow: false,
          ...(this.attr(`display-plot-style`) === DisplayPlotStyle.Stacked && {
            stacking: this.attr(`display-stacking-type`),
          }),
        },
      },
      // @ts-ignore (type is checked in `formatSeries`, but had to omit 'type' key there to allow either 'area' or 'line')
      series,
      time: {
        useUTC: false,
      },
      tooltip: {
        backgroundColor: null,
        borderWidth: 0,
        formatter(this) {
          // Highcharts callbacks don't work with arrow functions
          const {index, name} = this.series;
          const {color} = this.series.options;
          const x = this.x as number;

          // Override style to make the tooltip not look like the default
          if (thisComponent.formatTooltip) {
            return thisComponent.formatTooltip(x, this.y, this.series, index, this);
          }

          const formattedLeftLabel = thisComponent.formatPointX
            ? thisComponent.formatPointX(x)
            : Highcharts.dateFormat(`%b %e, %Y`, x);
          const formattedValue = thisComponent.formatPointY
            ? thisComponent.formatPointY(this.y)
            : this.y.toLocaleString();
          const seriesLabel = name;

          const defaultLabelFormatter = `
            <div class="row">
              <span class="value" style="font-weight: 700">${htmlEncodeString(formattedLeftLabel)}:</span>
              <span class="value">${formattedValue}</span>
            </div>
          `;
          return `
            <div class="line-chart-tooltip">
              <div class="title" style="background-color: ${color};">${seriesLabel}</div>
              <div class="result">
                ${defaultLabelFormatter}
              </div>
            </div>
          `;
        },
        hideDelay: 0,
        padding: 0,
        positioner(this, tooltipWidth, tooltipHeight, point) {
          const chart = this.chart;
          return getTooltipPosition({
            tooltipWidth,
            tooltipHeight,
            pointX: point.plotX,
            pointY: point.plotY,
            chartLeft: chart.plotLeft,
            chartTop: chart.plotTop,
            chartWidth: chart.plotWidth,
            chartHeight: chart.plotHeight,
          });
        },
        shadow: false,
        shape: `square`,
        useHTML: true,
      },
      title: {
        text: null,
      },
      xAxis: {
        endOnTick: false,
        labels: {
          enabled: true,
          staggerLines: xAxisOptions?.labels?.staggerLines,
          step: xAxisOptions?.labels?.step,
          ...(xAxisOptions?.labels?.style ? {style: xAxisOptions?.labels?.style} : {}),
          ...(this.formatXAxisLabel
            ? {
                formatter(this) {
                  return thisComponent.formatXAxisLabel(this.value as number);
                },
              }
            : {
                formatter(this) {
                  if (xAxisOptions?.unit === XAxisUnit.Number) {
                    return this.value as string;
                  }
                  return thisComponent.defaultXAxisLabelFormatter(this.value as number, timeUnit);
                },
              }),
        },
        crosshair: xAxisOptions?.crosshair,
        lineWidth: 1,
        maxPadding: 0,
        minPadding: 0,
        minTickInterval: MS_BY_UNIT[this.attr(`time-unit`)],
        plotBands: xAxisOptions?.plotBands,
        plotLines: xAxisOptions?.plotLines,
        tickPosition: `outside`,
        tickPositions: xAxisOptions?.tickPositions,
        tickPositioner(this) {
          const {dataMax, dataMin} = this.getExtremes();
          return thisComponent.formatTicksX ? thisComponent.formatTicksX(dataMin, dataMax) : undefined;
        },
        tickLength: 6,
        tickmarkPlacement: `on`,
        title: {
          text: null,
        },
        type: xAxisOptions?.unit === XAxisUnit.Number ? `linear` : `datetime`,
      },
      yAxis: {
        allowDecimals: true,
        endOnTick: true,
        gridLineColor: null,
        gridLineDashStyle: `Solid`,
        labels: {
          enabled: !this.attr(`hide-y-axis-ticks`),
          ...(this.attr(`y-axis-label-alignment`) ? {align: this.attr(`y-axis-label-alignment`)} : {}),
          ...(this.attr(`y-axis-label-horizontal-offset`) ? {x: this.attr(`y-axis-label-horizontal-offset`)} : {}),
          ...(this.attr(`y-axis-label-vertical-offset`) ? {y: this.attr(`y-axis-label-vertical-offset`)} : {}),
          formatter(this) {
            return thisComponent.formatYAxisLabel
              ? thisComponent.formatYAxisLabel(this.value as number)
              : formatMetricNumber(this.value as number);
          },
        },
        // maxPadding is relative to axis length which we approximate with chartHeight
        // to produce an absolute max padding of ~5px (but slightly less because chartHeight != y-axis length)
        maxPadding: yAxisOptions?.maxPadding ?? 5 / (this.chartHeight || 100),
        minPadding: 0,
        lineWidth: 0,
        softMin: yAxisOptions?.softMin ?? undefined,
        softMax: yAxisOptions?.softMax ?? undefined,
        startOnTick: false,
        title: {
          text: null,
        },
        type:
          this.attr(`display-analysis-type`) === DisplayAnalysisType.Logarithmic
            ? DisplayAnalysisType.Logarithmic
            : DisplayAnalysisType.Linear,
      },
    };
  }

  // bind method with arrow syntax because it's expected to be called from within a Highcharts callback
  updateHoveredTimestampMs = (x: null | number) => {
    if (this.hoveredTimestampMs === x) {
      return;
    }

    this.hoveredTimestampMs = x;
    if (this.annotations) {
      this.annotations.redrawCreateButton();
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseMove = (ev: MouseEvent) => {
    // If hovering over x-axis labels (which is naturally the area below the minimum y-value), manually update the "create annotation" hover position
    const yAxis = this.highcharts.yAxis[0];
    const y = yAxis.toValue(ev.offsetY, false);
    if (y < yAxis.min) {
      const extendedMouseEvent = this.highcharts.pointer.normalize(ev);
      const nearestPoint = this.highcharts.pointer.findNearestKDPoint(
        this.highcharts.series,
        false,
        extendedMouseEvent,
      );
      if (nearestPoint && this.hoveredTimestampMs !== nearestPoint.x) {
        this.updateHoveredTimestampMs(nearestPoint.x);
      }
    }
  };

  // bind method with arrow syntax because it's expected to be used as a DOM event listener
  handleHighchartsContainerMouseLeave = () => {
    this.updateHoveredTimestampMs(null);
  };

  /** Fixes a bug for short viewports: scrolling the report container while hovering the chart does not update
   * highcharts' internal "pointer", resulting in an offset until it is reset by a mouseleave event
   */
  handleReportScrollContainerScroll = debounce(() => {
    if (this.highcharts?.pointer) {
      // This is how highcharts internally clears the cached chart position, and may change in the future
      // @ts-ignore Property 'chartPosition' does not exist on type 'Pointer'.
      delete this.highcharts.pointer.chartPosition;
    }
  }, 100);
}

export const LineChartEl = defineAndMakeJsxFactory(`mp-line-chart`, LineChart);
export const LineChartReact = panel2react(`mp-line-chart`, LineChart);
